import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

// All routes under /embed/* can ONLY be accessed in iframes
const EMBEDDED_ROUTE_PREFIX = '/embed';

type EmbeddedContext = {
  /**
   * Whether the app is currently running inside an iframe.
   * Detected by comparing `window.self !== window.top`.
   */
  isEmbedded: boolean;
  /**
   * The origin (protocol + host + port) of the parent window that contains this iframe.
   * Extracted from `document.referrer` when available.
   * `null` if not embedded, no referrer is available, or extraction fails.
   */
  parentOrigin: string | null;
  /**
   * Whether the current request should be blocked from rendering.
   * True in the following cases:
   * - Embedded in an iframe but accessing a non-embedded route (regular routes cannot be embedded)
   * - Embedded in an iframe but from an unauthorized origin
   * - Not embedded but trying to access an embedded-only route (`/embed/*`)
   */
  shouldBlock: boolean;
  /**
   * Whether the current route path starts with `/embed/`.
   * Embedded routes can only be accessed when properly embedded in an authorized iframe.
   */
  isEmbeddedRoute: boolean;
};

/**
 * Calculate the embedding context based on current state.
 * This is extracted into a function so it can be called both during initialization
 * and when the location changes.
 */
const calculateEmbeddedContext = (pathname: string): EmbeddedContext => {
  const isEmbedded = window.self !== window.top;
  const isEmbeddedRoute = pathname.startsWith(EMBEDDED_ROUTE_PREFIX);

  // Case 1: Not embedded - block if trying to access /embed/* routes
  if (!isEmbedded) {
    const shouldBlock = isEmbeddedRoute;
    return {
      isEmbedded: false,
      parentOrigin: null,
      shouldBlock,
      isEmbeddedRoute,
    };
  }

  // Case 2: Embedded - validate origin and route
  let parentOrigin: string | null = null;
  try {
    if (document.referrer) {
      parentOrigin = new URL(document.referrer).origin;
    }
  } catch (e) {
    console.warn('Could not determine parent origin:', e);
  }

  const shouldBlock = !isEmbeddedRoute; // Block if: wrong route

  // Debug logging in development
  if (import.meta.env.DEV) {
    console.group('[useIsEmbedded] Iframe Detection');
    console.log('✓ Embedded:', true);
    console.log('📍 Current Path:', pathname);
    console.log('🛣️  Is Embedded Route:', isEmbeddedRoute);
    console.log('🌐 Parent Origin:', parentOrigin || '(no referrer)');
    console.log('🎯 Final Result:', {
      shouldBlock: shouldBlock ? '🚫 BLOCKED' : '✓ Allowed',
    });
    console.groupEnd();
  }

  return {
    isEmbedded: true,
    parentOrigin,
    shouldBlock,
    isEmbeddedRoute,
  };
};

/**
 * Hook to detect if the app is running within an iframe and validate
 * whether the embedding is authorized based on the parent origin and current route.
 *
 * Requires React Router context (must be called within a Router provider).
 * Uses useLocation to properly detect route changes via React Router navigation.
 *
 * @returns {EmbeddedContext} Context object with embedding information
 * @throws {Error} If called outside of a React Router context
 */
export const useIsEmbedded = (): EmbeddedContext => {
  const location = useLocation();
  const [context, setContext] = useState<EmbeddedContext>(() =>
    calculateEmbeddedContext(location.pathname)
  );

  useEffect(() => {
    const newContext = calculateEmbeddedContext(location.pathname);
    setContext(newContext);
  }, [location.pathname]);

  return context;
};
